// context/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { jwtDecode } from 'jwt-decode';
import { AuthContextType, AuthState, User, Deployment } from '../types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const BACKEND_URL = "https://instant-deployerapi.pionedata.com";

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [authState, setAuthState] = useState<AuthState>('checking');
  const [userEmail, setUserEmail] = useState<string>('');
  const [deployments, setDeployments] = useState<Deployment[]>([]);

  // Check if token is expired
  const isTokenExpired = (token: string): boolean => {
    try {
      const decoded: any = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      return decoded.exp < currentTime;
    } catch (error) {
      console.error('Error decoding token:', error);
      return true;
    }
  };

  // Check authentication status and clear expired tokens
  const checkAuthStatus = (): boolean => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      return false;
    }
    
    if (isTokenExpired(token)) {
      console.log('Token expired, clearing storage...');
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_email');
      setAuthState('login');
      setUser(null);
      setUserEmail('');
      setDeployments([]);
      return false;
    }
    
    return true;
  };

  useEffect(() => {
    console.log('AuthContext: Initializing...');
    const token = localStorage.getItem('access_token');
    const email = localStorage.getItem('user_email');
    
    if (token && !isTokenExpired(token)) {
      console.log('Valid token found, setting authenticated state');
      setAuthState('authenticated');
      if (email) {
        setUserEmail(email);
      }
      fetchDeployments();
    } else {
      console.log('No valid token found, clearing storage and setting login state');
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_email');
      setAuthState('login');
    }
  }, []);

  // Periodic token validation (check every 5 minutes)
  useEffect(() => {
    if (authState !== 'authenticated') return;
    
    const interval = setInterval(() => {
      if (!checkAuthStatus()) {
        console.log('Session expired during periodic check');
        logout();
      }
    }, 5 * 60 * 1000); // 5 minutes
    
    return () => clearInterval(interval);
  }, [authState]);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      const response = await fetch(`${BACKEND_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Login failed');
      }

      setUserEmail(email);
      setAuthState('otp');
    } catch (error) {
      throw error;
    }
  };

  const verifyOTP = async (email: string, otp: string): Promise<void> => {
    try {
      const response = await fetch(`${BACKEND_URL}/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, otp }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'OTP verification failed');
      }

      const data = await response.json();
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('user_email', email);
      setUserEmail(email);
      setAuthState('authenticated');
      
      // Force navigation to dashboard
      window.history.pushState({}, '', '/dashboard');
      
      await fetchDeployments();
    } catch (error) {
      throw error;
    }
  };

  const fetchDeployments = async (): Promise<void> => {
    try {
      if (!checkAuthStatus()) {
        logout();
        return;
      }
      
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const response = await fetch(`${BACKEND_URL}/deployments`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.status === 401) {
        console.log('401 Unauthorized - token invalid or expired');
        logout();
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        setDeployments(data.deployments);
      }
    } catch (error) {
      console.error('Failed to fetch deployments:', error);
    }
  };

  const logout = (): void => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_email');
    setAuthState('login');
    setUser(null);
    setUserEmail('');
    setDeployments([]);
    // Force redirect to login if not already there
    if (window.location.pathname !== '/') {
      window.location.href = '/';
    }
  };

  const value: AuthContextType = {
    user,
    authState,
    userEmail,
    isLoggedIn: authState === 'authenticated',
    isLoading: authState === 'checking',
    showOtpForm: authState === 'otp',
    login,
    verifyOTP,
    logout,
    deployments,
    fetchDeployments,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};